/**
 * Credits & Fees — Accounting tab (2026-10). Two sections:
 *   1. Retained Cancellation Fees — simple list, no GST/TCS, no invoice,
 *      "tax treatment pending CA" (routes/cancellation.py, admin/accounts only).
 *   2. Client Wallet Credits — every client_wallet_credit note across all
 *      travellers (routes/credit_notes.py `GET /api/credit-notes`).
 * Agents have zero access to this tab (gated in Accounting.js/Sidebar).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, ReceiptText, Wallet, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

const AccountingCreditsFees = () => {
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState({ rows: [], total: 0 });
  const [credits, setCredits] = useState([]);
  const [search, setSearch] = useState('');

  const describeApplication = (app) => {
    const amountLabel = inr(app.amount);
    if (app.crossTraveller) {
      return `${amountLabel} to ${app.targetTravellerName} (${app.relationship})`;
    }
    return amountLabel;
  };

  const describeApplications = (applications) => {
    const list = applications || [];
    return list.map(describeApplication).join(', ');
  };

  const load = async () => {
    setLoading(true);
    try {
      const [feeRes, creditRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cancellations/retained-fees`),
        axios.get(`${BACKEND_URL}/api/credit-notes`, { params: { direction: 'client_wallet_credit' } }),
      ]);
      setFees(feeRes.data || { rows: [], total: 0 });
      setCredits(creditRes.data || []);
    } catch (err) {
      toast.error('Failed to load Credits & Fees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredCredits = credits.filter((c) =>
    !search.trim() || (c.clientName || '').toLowerCase().includes(search.toLowerCase()) || (c.clientUCC || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="credits-fees-loading"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="accounting-credits-fees-tab">
      {/* ---- Section 1: Retained Cancellation Fees ---- */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
          <ReceiptText className="h-4 w-4 text-teal-700" />Retained Cancellation Fees
          <Badge className="bg-slate-100 text-slate-600">{inr(fees.total)} total</Badge>
        </h2>
        <p className="text-xs text-slate-400 mt-0.5">
          No GST/TCS calculated or posted, no invoice raised — tax treatment pending CA.
        </p>
        {fees.rows.length === 0 ? (
          <Card className="p-8 text-center mt-3" data-testid="retained-fees-empty-state">
            <ShieldCheck className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No retained cancellation fees recorded yet.</p>
          </Card>
        ) : (
          <div className="space-y-2 mt-3" data-testid="retained-fees-list">
            {(fees.rows || []).map((f) => (
              <Card key={f.feeId} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`retained-fee-row-${f.feeId}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {f.clientName} <span className="text-slate-400 font-mono text-xs">· {f.displayTripId}</span>
                  </div>
                  <div className="text-[11px] text-slate-400">{f.reason} · {fmtDate(f.date)} · recorded by {f.recordedBy}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] uppercase">tax pending CA</Badge>
                  <span className="text-sm font-bold text-teal-700">{inr(f.amount)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ---- Section 2: Client Wallet Credits ---- */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
          <Wallet className="h-4 w-4 text-teal-700" />Client Credits
        </h2>
        <p className="text-xs text-slate-400 mt-0.5">
          Wallet credit is not cash — excluded from receipt totals and Tally exports. No "convert to cash" action exists.
        </p>
        <Input
          placeholder="Search by traveller name or UCC…"
          className="w-64 h-8 text-xs mt-3"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="credits-fees-search-input"
        />
        {filteredCredits.length === 0 ? (
          <Card className="p-8 text-center mt-3" data-testid="client-credits-empty-state">
            <ShieldCheck className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No client wallet credits recorded yet.</p>
          </Card>
        ) : (
          <div className="space-y-2 mt-3" data-testid="client-credits-list">
            {filteredCredits.map((c) => (
              <Card key={c.creditNoteId} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`client-credit-row-${c.creditNoteId}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {c.clientName} <span className="text-slate-400 font-mono text-xs">· {c.clientUCC}</span>
                  </div>
                  <div className="text-[11px] text-slate-400">{c.reason} · {fmtDate(c.createdAt)} · {c.creditNoteNumber}</div>
                  {(c.applications || []).length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1">
                      {c.applications.length} application(s) — {describeApplications(c.applications)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 text-right">
                  <Badge className={c.status === 'open' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'}>
                    {c.status}
                  </Badge>
                  <div>
                    <div className="text-sm font-bold text-teal-700">{inr(c.remainingAmount)} left</div>
                    <div className="text-[11px] text-slate-400">of {inr(c.originalAmount)}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingCreditsFees;
